import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAppContext, JOB_TYPES, JOB_CATEGORIES } from '../../context/AppContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import RichTextEditor from '../../components/common/RichTextEditor';

const { 
  FiPlus, FiX, FiCheck, FiInfo, FiZap, FiTarget, 
  FiMapPin, FiTag, FiShoppingBag, FiScissors, 
  FiLoader, FiAlertCircle, FiMail, FiLink, FiFileText,
  FiShield, FiUserPlus, FiLogIn, FiArrowRight, FiCheckCircle,
  FiUpload, FiImage, FiStar
} = FiIcons;

const AuthGate = ({ onAuthSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);

  const { signIn, signUp, uploadLogo } = useAppContext();
  const [spamChallenge, setSpamChallenge] = useState({ a: 0, b: 0 });
  const [userSpamAnswer, setUserSpamAnswer] = useState('');

  const generateChallenge = useCallback(() => {
    setSpamChallenge({
      a: Math.floor(Math.random() * 10) + 1,
      b: Math.floor(Math.random() * 10) + 1
    });
    setUserSpamAnswer('');
  }, []);

  useEffect(() => {
    if (!isLogin) generateChallenge();
  }, [isLogin, generateChallenge]);

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedLogo(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || (!isLogin && !orgName)) {
      setError('Please fill in all fields');
      return;
    }

    if (!isLogin) {
      const correctAnswer = spamChallenge.a + spamChallenge.b;
      if (parseInt(userSpamAnswer) !== correctAnswer) {
        setError('Incorrect verification answer. Please try again.');
        generateChallenge();
        return;
      }
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      if (isLogin) {
        const result = await signIn(email, password);
        if (result.success) onAuthSuccess();
        else { setError(result.error); setIsSubmitting(false); }
      } else {
        let uploadedLogoUrl = null;
        if (selectedLogo) {
          const tempId = `reg-${Date.now()}`;
          const uploadResult = await uploadLogo(selectedLogo, tempId);
          if (uploadResult.success) uploadedLogoUrl = uploadResult.url;
        }

        const result = await signUp(email, password, orgName, uploadedLogoUrl, honeypot);
        if (result.success) {
          if (result.needsConfirmation) {
            setSuccessMsg('Account created! Please check your email to confirm your account.');
            setIsSubmitting(false);
          } else {
            onAuthSuccess();
          }
        } else {
          setError(result.error);
          setIsSubmitting(false);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred.');
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto bg-white rounded-[40px] shadow-xl p-10 text-center relative overflow-hidden border border-stone-100">
      <div className="absolute top-0 left-0 w-full h-2 bg-emerald-500" />
      {successMsg ? (
        <div className="py-8">
          <div className="w-20 h-20 bg-emerald-50 rounded-3xl flex items-center justify-center mx-auto mb-8 border border-emerald-100">
            <SafeIcon icon={FiCheckCircle} className="text-4xl text-emerald-600" />
          </div>
          <h3 className="text-2xl font-black text-stone-900 uppercase tracking-tighter mb-4">Account Created!</h3>
          <p className="text-stone-500 font-bold text-xs uppercase tracking-widest leading-relaxed mb-6">{successMsg}</p>
          <Link to="/" className="w-full block py-4 bg-stone-900 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-stone-800 transition-all text-center">Return Home</Link>
        </div>
      ) : (
        <>
          <div className="w-20 h-20 bg-emerald-50 rounded-3xl flex items-center justify-center mx-auto mb-8 border border-emerald-100">
            <SafeIcon icon={isLogin ? FiLogIn : FiUserPlus} className="text-4xl text-emerald-600" />
          </div>
          <h3 className="text-2xl font-black text-stone-900 uppercase tracking-tighter mb-2">{isLogin ? 'Welcome Back' : 'Join the Expedition'}</h3>
          <p className="text-stone-500 font-bold text-[10px] uppercase tracking-widest mb-8">{isLogin ? 'Sign in to start posting your job' : 'Create an account to start posting your job'}</p>
          <form onSubmit={handleSubmit} className="space-y-4 text-left">
            {error && <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-[10px] font-bold uppercase tracking-wider">{error}</div>}
            {!isLogin && (
              <>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-widest text-stone-400 ml-1">Organization Logo (Optional)</label>
                  <div onClick={() => fileInputRef.current?.click()} className="relative group cursor-pointer">
                    <div className="h-28 w-full bg-stone-50 rounded-xl flex items-center justify-center overflow-hidden border-2 border-dashed border-stone-200 group-hover:border-emerald-500 transition-all p-3">
                      {logoPreview ? <img src={logoPreview} alt="Logo Preview" className="max-h-full w-auto object-contain" /> : <div className="flex flex-col items-center gap-1"><SafeIcon icon={FiImage} className="text-2xl text-stone-300" /><span className="text-[8px] font-black uppercase text-stone-400 tracking-widest text-center">Click to upload brand logo</span></div>}
                    </div>
                    <input type="file" ref={fileInputRef} onChange={handleLogoSelect} className="hidden" accept="image/*" />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-widest text-stone-400 ml-1">Organization Name</label>
                  <input type="text" required className="w-full px-4 py-3 bg-stone-50 border border-stone-100 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all text-sm" value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="Adventure Tours LLC" />
                </div>
                <input 
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={e => setHoneypot(e.target.value)}
                  style={{ display: 'none !important' }}
                  className="hidden"
                  tabIndex="-1"
                  autoComplete="off"
                />
              </>
            )}
            <div className="space-y-1"><label className="text-[10px] font-black uppercase tracking-widest text-stone-400 ml-1">Email Address</label><input type="email" required className="w-full px-4 py-3 bg-stone-50 border border-stone-100 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all text-sm" value={email} onChange={e => setEmail(e.target.value)} /></div>
            <div className="space-y-1"><label className="text-[10px] font-black uppercase tracking-widest text-stone-400 ml-1">Password</label><input type="password" required className="w-full px-4 py-3 bg-stone-50 border border-stone-100 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all text-sm" value={password} onChange={e => setPassword(e.target.value)} /></div>
            {!isLogin && (
              <div className="p-4 bg-stone-50 rounded-xl border border-stone-100 space-y-3">
                <div className="flex items-center gap-2 text-stone-900 font-bold text-[10px] uppercase tracking-widest"><SafeIcon icon={FiShield} className="text-emerald-500" /><h4>Spam Protection</h4></div>
                <div className="flex items-center gap-4"><div className="text-sm font-black text-emerald-600">{spamChallenge.a} + {spamChallenge.b} =</div><input type="number" required placeholder="?" className="w-20 px-3 py-2 bg-white border border-stone-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none transition-all text-sm" value={userSpamAnswer} onChange={e => setUserSpamAnswer(e.target.value)} /></div>
              </div>
            )}
            <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center gap-3 py-4 bg-emerald-600 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-emerald-500 transition-all shadow-xl shadow-emerald-100 mt-4 disabled:opacity-50">
              {isSubmitting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : (isLogin ? 'Sign In' : 'Create Account')}
              {!isSubmitting && <SafeIcon icon={FiArrowRight} />}
            </button>
            <p className="text-center text-[10px] font-bold uppercase tracking-widest text-stone-400 mt-6">{isLogin ? "Don't have an account? " : "Already have an account? "}<button type="button" onClick={() => { setIsLogin(!isLogin); setError(''); }} className="text-emerald-600 hover:text-emerald-500">{isLogin ? 'Sign Up' : 'Sign In'}</button></p>
          </form>
        </>
      )}
    </motion.div>
  );
};

export default function PostJob() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, profile, addJob, updateJob, jobs, products, createCheckoutSession } = useAppContext();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '', description: '', job_type: 'Bicycle', category: 'Seasonal',
    application_method: 'link', application_link: '', application_instructions: '',
    product_id: '', coupon_code: ''
  });

  const [honeypot, setHoneypot] = useState('');
  const [locations, setLocations] = useState([{ name: '' }]);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [spamChallenge, setSpamChallenge] = useState({ a: 0, b: 0 });
  const [userSpamAnswer, setUserSpamAnswer] = useState('');
  
  const turnstileRef = useRef(null);
  const widgetId = useRef(null);

  useEffect(() => {
    // Manually render Turnstile when we reach the final step
    if (step === 3 && turnstileRef.current && window.turnstile) {
      try {
        // If there's an existing widget, remove it first
        if (widgetId.current) {
          window.turnstile.remove(widgetId.current);
        }
        
        widgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY,
          theme: 'light',
          callback: (token) => {
            console.log('Turnstile token received');
          },
        });
      } catch (err) {
        console.error('Turnstile render error:', err);
      }
    }

    return () => {
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
  }, [step]);

  useEffect(() => {
    if (id && jobs.length > 0) {
      const jobToEdit = jobs.find(j => j.id === id);
      if (jobToEdit) {
        setIsEditMode(true);
        setFormData({
          title: jobToEdit.title || '', description: jobToEdit.description || '',
          job_type: jobToEdit.job_type || 'Bicycle', category: jobToEdit.category || 'Seasonal',
          application_method: jobToEdit.application_method || 'link',
          application_link: jobToEdit.application_link || '',
          application_instructions: jobToEdit.application_instructions || '',
          product_id: jobToEdit.product_id || '', coupon_code: jobToEdit.coupon_code || ''
        });
        if (jobToEdit.locations) setLocations(jobToEdit.locations.map(l => ({ name: l.name })));
        if (jobToEdit.tags) setTags(jobToEdit.tags.map(t => t.tag));
      }
    }
  }, [id, jobs]);

  useEffect(() => {
    if (step === 3 && !formData.product_id && !isEditMode) generateChallenge();
  }, [step, formData.product_id, isEditMode]);

  const generateChallenge = () => {
    setSpamChallenge({ a: Math.floor(Math.random() * 10) + 1, b: Math.floor(Math.random() * 10) + 1 });
    setUserSpamAnswer('');
  };

  const handleLocationChange = (index, value) => {
    const newLocs = [...(locations || [])];
    if (newLocs[index]) {
      newLocs[index].name = value;
      setLocations(newLocs);
    }
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && newTag.trim()) {
      e.preventDefault();
      if (!tags.includes(newTag.trim())) {
        setTags([...tags, newTag.trim()]);
      }
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('handleSubmit initiated');
    
    try {
      // Get the token from the specific widget we rendered
      const turnstileToken = widgetId.current ? window.turnstile?.getResponse(widgetId.current) : null;
      
      if (!turnstileToken) {
        setError('Please complete the verification');
        return;
      }

      if (!formData.product_id && !isEditMode) {
        const correctAnswer = spamChallenge.a + spamChallenge.b;
        if (parseInt(userSpamAnswer) !== correctAnswer) {
          setError('Incorrect verification answer.');
          generateChallenge();
          if (widgetId.current) window.turnstile?.reset(widgetId.current);
          return;
        }
      }
      setLoading(true);
      setError(null);
      
      const validLocs = (locations || []).filter(l => l && l.name && l.name.trim());
      
      if (isEditMode) {
        const { product_id, coupon_code, ...updateData } = formData;
        const result = await updateJob(id, updateData, validLocs, tags);
        if (result.success) navigate('/employer/dashboard');
        else throw new Error(result.error);
      } else {
        const result = await addJob(formData, validLocs, tags, honeypot, turnstileToken);
        if (result.success) {
          if (formData.product_id && result.job) {
            const successUrl = `${window.location.origin}/#/employer/dashboard?payment=success`;
            const cancelUrl = `${window.location.origin}/#/employer/dashboard?payment=cancelled`;
            const checkoutData = await createCheckoutSession(result.job.id, formData.product_id, formData.coupon_code, successUrl, cancelUrl);
            window.location.href = checkoutData.url;
          } else navigate('/employer/dashboard');
        } else {
          throw new Error(result.error);
        }
      }
    } catch (err) {
      console.error('Job submission failed:', err);
      setError(err.message);
      setLoading(false);
      if (widgetId.current) window.turnstile?.reset(widgetId.current);
    }
  };

  if (!user && !isEditMode) return <div className="min-h-screen pt-32 pb-12 px-4 bg-gray-50 flex items-center justify-center"><AuthGate onAuthSuccess={() => {}} /></div>;

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 bg-gray-50">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">{isEditMode ? 'Edit Job' : 'Post a Job'}</h1>
          <span className="text-sm font-medium text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">Step {step} of {isEditMode ? '2' : '3'}</span>
        </div>
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm flex items-center gap-3">
            <SafeIcon icon={FiAlertCircle} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {step === 1 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Job Title</label>
                <input required type="text" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="e.g. Lead Wilderness Guide" />
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">Type</label>
                  <select className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none" value={formData.job_type} onChange={e => setFormData({ ...formData, job_type: e.target.value })}>
                    {JOB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">Category</label>
                  <select className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none" value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}>
                    {JOB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Description</label>
                <RichTextEditor 
                  value={formData.description} 
                  onChange={val => setFormData({ ...formData, description: val })} 
                  className="focus-within:ring-emerald-500"
                />
              </div>

              <div className="space-y-4 pt-4 border-t border-gray-100">
                <h3 className="font-bold flex items-center gap-2"><SafeIcon icon={FiMail} className="text-emerald-600" /> Application Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Method</label>
                    <select className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none" value={formData.application_method} onChange={e => setFormData({ ...formData, application_method: e.target.value })}>
                      <option value="link">External URL</option>
                      <option value="email">Email Address</option>
                      <option value="text">Custom Instructions</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      {formData.application_method === 'link' ? 'Application URL' : formData.application_method === 'email' ? 'Contact Email' : 'Short Note'}
                    </label>
                    <input required type={formData.application_method === 'email' ? 'email' : 'text'} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none" value={formData.application_link} onChange={e => setFormData({ ...formData, application_link: e.target.value })} placeholder={formData.application_method === 'link' ? 'https://...' : formData.application_method === 'email' ? 'jobs@company.com' : 'Apply via our portal'} />
                  </div>
                </div>
                {formData.application_method === 'text' && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Detailed Instructions</label>
                    <textarea rows={3} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none" value={formData.application_instructions} onChange={e => setFormData({ ...formData, application_instructions: e.target.value })} placeholder="List any specific steps or documents required..." />
                  </div>
                )}
              </div>

              <input 
                type="text"
                name="website"
                value={honeypot}
                onChange={e => setHoneypot(e.target.value)}
                style={{ display: 'none !important' }}
                className="hidden"
                tabIndex="-1"
                autoComplete="off"
              />

              <div className="flex justify-end pt-4">
                <button type="button" onClick={() => setStep(2)} className="bg-emerald-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-emerald-700 transition-colors flex items-center gap-2">
                  Next <SafeIcon icon={FiArrowRight} />
                </button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
              <div className="bg-white p-8 rounded-2xl border border-gray-100 space-y-6">
                <div className="space-y-4">
                  <h3 className="font-bold flex items-center gap-2"><SafeIcon icon={FiMapPin} className="text-emerald-600" /> Locations</h3>
                  <div className="space-y-3">
                    {(locations || []).map((loc, i) => (
                      <div key={i} className="flex gap-2">
                        <input className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500" value={loc?.name || ''} onChange={e => handleLocationChange(i, e.target.value)} placeholder="City, State / Remote" />
                        {(locations || []).length > 1 && (
                          <button type="button" onClick={() => setLocations(locations.filter((_, idx) => idx !== i))} className="p-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors">
                            <SafeIcon icon={FiX} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={() => setLocations([...(locations || []), { name: '' }])} className="text-emerald-600 text-sm font-bold flex items-center gap-1 hover:text-emerald-700 transition-colors">
                    <SafeIcon icon={FiPlus} /> Add Another Location
                  </button>
                </div>

                <div className="space-y-4 pt-6 border-t border-gray-100">
                  <h3 className="font-bold flex items-center gap-2"><SafeIcon icon={FiTag} className="text-emerald-600" /> Perks & Tags</h3>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {tags.map(tag => (
                      <span key={tag} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-bold uppercase tracking-wider">
                        {tag}
                        <button type="button" onClick={() => removeTag(tag)} className="hover:text-emerald-900">
                          <SafeIcon icon={FiX} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <input type="text" className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500" value={newTag} onChange={e => setNewTag(e.target.value)} onKeyDown={handleAddTag} placeholder="Add a perk (e.g. Housing Provided) and press Enter" />
                </div>
              </div>

              <div className="flex justify-between items-center">
                <button type="button" onClick={() => setStep(1)} className="text-gray-600 font-bold hover:text-gray-900 transition-colors">Back</button>
                <button type="button" onClick={() => setStep(3)} className="bg-emerald-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-emerald-700 transition-colors">
                  {isEditMode ? 'Review & Save' : 'Next'}
                </button>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div onClick={() => setFormData({ ...formData, product_id: '' })} className={`p-8 rounded-3xl border-2 transition-all cursor-pointer relative overflow-hidden ${!formData.product_id ? 'border-emerald-500 bg-emerald-50' : 'border-gray-100 bg-white hover:border-emerald-200'}`}>
                  {!formData.product_id && <div className="absolute top-4 right-4 text-emerald-600"><SafeIcon icon={FiCheckCircle} className="text-xl" /></div>}
                  <h3 className="font-black text-2xl text-stone-900 uppercase tracking-tighter mb-1">Standard</h3>
                  <p className="text-emerald-600 font-bold text-xs uppercase tracking-widest mb-6">Free Listing</p>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2 text-stone-500 text-xs font-bold uppercase tracking-wider"><SafeIcon icon={FiCheck} className="text-emerald-500" /> 30-Day Listing</li>
                    <li className="flex items-center gap-2 text-stone-500 text-xs font-bold uppercase tracking-wider"><SafeIcon icon={FiCheck} className="text-emerald-500" /> Standard Map Pin</li>
                    <li className="flex items-center gap-2 text-stone-300 text-xs font-bold uppercase tracking-wider"><SafeIcon icon={FiX} /> Pinned to Top</li>
                  </ul>
                </div>

                {(products || []).map(p => (
                  <div key={p.id} onClick={() => setFormData({ ...formData, product_id: p.id })} className={`p-8 rounded-3xl border-2 transition-all cursor-pointer relative overflow-hidden ${formData.product_id === p.id ? 'border-emerald-500 bg-emerald-50 shadow-lg shadow-emerald-100/50' : 'border-gray-100 bg-white hover:border-emerald-200'}`}>
                    {formData.product_id === p.id && <div className="absolute top-4 right-4 text-emerald-600"><SafeIcon icon={FiCheckCircle} className="text-xl" /></div>}
                    <div className="absolute top-0 right-0 p-3"><SafeIcon icon={FiStar} className="text-emerald-500/20 text-4xl" /></div>
                    <h3 className="font-black text-2xl text-stone-900 uppercase tracking-tighter mb-1">{p.name}</h3>
                    <p className="text-emerald-600 font-bold text-xs uppercase tracking-widest mb-6">${p.price_cents / 100}</p>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-2 text-stone-600 text-xs font-bold uppercase tracking-wider"><SafeIcon icon={FiCheck} className="text-emerald-500" /> {p.extended_duration ? '60-Day' : '30-Day'} Listing</li>
                      {p.pin_to_top && <li className="flex items-center gap-2 text-stone-600 text-xs font-bold uppercase tracking-wider"><SafeIcon icon={FiCheck} className="text-emerald-500" /> Pinned to Top</li>}
                      {p.featured_map_pin && <li className="flex items-center gap-2 text-stone-600 text-xs font-bold uppercase tracking-wider"><SafeIcon icon={FiCheck} className="text-emerald-500" /> Featured Map Icon</li>}
                      {p.newsletter_inclusion && <li className="flex items-center gap-2 text-stone-600 text-xs font-bold uppercase tracking-wider"><SafeIcon icon={FiCheck} className="text-emerald-500" /> Newsletter Feature</li>}
                    </ul>
                  </div>
                ))}
              </div>

              {!formData.product_id && (
                <div className="bg-white p-8 rounded-3xl border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-6">
                  <div>
                    <h4 className="font-black text-stone-900 uppercase tracking-tighter mb-1">Human Verification</h4>
                    <p className="text-stone-500 font-bold text-[10px] uppercase tracking-widest">Help us keep the expedition bot-free</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-black text-emerald-600 text-xl tracking-tighter">{spamChallenge.a} + {spamChallenge.b} =</span>
                    <input type="number" className="w-24 px-4 py-3 border-2 border-stone-100 rounded-2xl focus:ring-2 focus:ring-emerald-500 outline-none text-center font-black" value={userSpamAnswer} onChange={e => setUserSpamAnswer(e.target.value)} />
                  </div>
                </div>
              )}

              {/* Turnstile Widget Container */}
              <div className="flex justify-center py-4">
                <div ref={turnstileRef}></div>
              </div>

              <div className="flex justify-between items-center">
                <button type="button" onClick={() => setStep(2)} className="text-gray-600 font-bold hover:text-gray-900 transition-colors">Back</button>
                <button disabled={loading} type="submit" className="bg-emerald-600 text-white px-12 py-4 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-emerald-500 transition-all shadow-xl shadow-emerald-100/50 disabled:opacity-50">
                  {loading ? (
                    <span className="flex items-center gap-2"><SafeIcon icon={FiLoader} className="animate-spin" /> Processing...</span>
                  ) : (
                    isEditMode ? 'Update Expedition' : (formData.product_id ? 'Pay & Post' : 'Post Free Listing')
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </form>
      </div>
    </div>
  );
}